import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Paper, Typography, Grid, Button, Avatar, Chip,
  TextField, Alert, CircularProgress, Divider, ToggleButton, ToggleButtonGroup,
  Card, CardContent, Stepper, Step, StepLabel
} from '@mui/material';
import {
  CalendarToday, AccessTime, ArrowBack, CheckCircle, Payment
} from '@mui/icons-material';
import { getTeacherPublicProfile } from '../../api/marketplace';
import { createBooking } from '../../api/booking';
import { getCurrentLanguage } from '../../utils/navigation';
import { useAuth } from '../../store/authStore';
import { detectTimezone } from '../../utils/timezone';

const BookLesson = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const lang = getCurrentLanguage();

  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [isTrial, setIsTrial] = useState(true);
  const [notes, setNotes] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');

  useEffect(() => {
    loadTeacher();
  }, [teacherId]);

  const loadTeacher = async () => {
    try {
      const profile = await getTeacherPublicProfile(teacherId);
      setTeacher(profile);
      if (profile.languages?.length > 0) {
        setSelectedLanguage(profile.languages[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate available dates (next 14 days) using local calendar dates
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayOfWeek = date.getDay();
      const hasSlots = teacher?.availability?.some(a => a.dayOfWeek === dayOfWeek && a.active);
      // Build YYYY-MM-DD from local date components to avoid UTC midnight off-by-one
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      dates.push({
        date: `${y}-${m}-${d}`,
        dayName: date.toLocaleDateString('en', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en', { month: 'short' }),
        available: hasSlots || teacher?.availability?.length === 0
      });
    }
    return dates;
  };

  // Generate time slots for selected date
  const getTimeSlots = () => {
    if (!selectedDate || !teacher) return [];
    // Parse as local noon to avoid DST/UTC-offset day-of-week errors
    const [y, mo, d] = selectedDate.split('-').map(Number);
    const dayOfWeek = new Date(y, mo - 1, d).getDay();
    const daySlots = teacher.availability?.filter(a => a.dayOfWeek === dayOfWeek && a.active);

    if (daySlots?.length > 0) {
      return daySlots.map(s => s.startTime);
    }

    // Default time slots if no availability is set
    return ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'];
  };

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'EUR': return '€';
      case 'MAD': return 'د.م.';
      case 'GBP': return '£';
      default: return '$';
    }
  };

  const getPrice = () => {
    if (isTrial && teacher?.trialPrice !== undefined) return teacher.trialPrice;
    return teacher?.pricePerHour || 0;
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select a date and time');
      return;
    }
    setBooking(true);
    setError('');
    try {
      // Calculate end time
      const duration = teacher.lessonDuration || 50;
      const [hours, mins] = selectedTime.split(':').map(Number);
      const endDate = new Date(2000, 0, 1, hours, mins + duration);
      const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

      await createBooking({
        studentId: user.id,
        teacherId: teacher.id,
        studentName: user.displayName || `${user.firstName} ${user.lastName}`,
        teacherName: teacher.displayName,
        date: selectedDate,
        startTime: selectedTime,
        endTime,
        duration,
        price: getPrice(),
        currency: teacher.currency || 'USD',
        language: selectedLanguage,
        isTrial,
        notes,
        // timezone is required so booking.js can compute startAtUTC / endAtUTC
        timezone: user.timezone || detectTimezone(),
      });

      setSuccess(true);
      setActiveStep(2);
    } catch (err) {
      setError(err.message || 'Failed to create booking');
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 20 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!teacher) {
    return (
      <Box sx={{ pt: 12, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto' }}>Teacher not found</Alert>
      </Box>
    );
  }

  const dates = getAvailableDates();
  const timeSlots = getTimeSlots();
  const price = getPrice();
  const currSymbol = getCurrencySymbol(teacher.currency);

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 10, pb: 4 }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>

        <Paper sx={{ p: 4 }}>
          {/* Teacher Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar src={teacher.photoURL} sx={{ width: 64, height: 64 }}>
              {teacher.displayName?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700}>{teacher.displayName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {teacher.languages?.join(', ')} · {currSymbol}{teacher.pricePerHour}/{teacher.lessonDuration || 50} min
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            <Step><StepLabel>Choose Time</StepLabel></Step>
            <Step><StepLabel>Confirm</StepLabel></Step>
            <Step><StepLabel>Done</StepLabel></Step>
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {/* Step 0: Select Date & Time */}
          {activeStep === 0 && (
            <Box>
              {/* Lesson Type */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Lesson Type</Typography>
              <ToggleButtonGroup
                value={isTrial} exclusive
                onChange={(_, v) => v !== null && setIsTrial(v)}
                sx={{ mb: 3 }}
              >
                <ToggleButton value={true}>
                  Trial Lesson ({teacher.trialPrice === 0 ? 'Free' : `${currSymbol}${teacher.trialPrice}`})
                </ToggleButton>
                <ToggleButton value={false}>
                  Regular Lesson ({currSymbol}{teacher.pricePerHour})
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Language */}
              {teacher.languages?.length > 1 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>Language</Typography>
                  <ToggleButtonGroup
                    value={selectedLanguage} exclusive
                    onChange={(_, v) => v && setSelectedLanguage(v)}
                  >
                    {teacher.languages.map(l => (
                      <ToggleButton key={l} value={l}>{l}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Date Selection */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                <CalendarToday sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Select a Date
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, overflow: 'auto', pb: 1, mb: 3 }}>
                {dates.map(d => (
                  <Card
                    key={d.date}
                    sx={{
                      minWidth: 75, cursor: d.available ? 'pointer' : 'default',
                      textAlign: 'center', p: 1,
                      border: selectedDate === d.date ? '2px solid #2196F3' : '1px solid #e0e0e0',
                      bgcolor: selectedDate === d.date ? '#E3F2FD' : d.available ? '#fff' : '#f5f5f5',
                      opacity: d.available ? 1 : 0.5,
                      transition: 'all 0.2s'
                    }}
                    onClick={() => d.available && setSelectedDate(d.date)}
                  >
                    <Typography variant="caption" color="text.secondary">{d.dayName}</Typography>
                    <Typography variant="h6" fontWeight={700}>{d.dayNum}</Typography>
                    <Typography variant="caption" color="text.secondary">{d.month}</Typography>
                  </Card>
                ))}
              </Box>

              {/* Time Selection */}
              {selectedDate && (
                <>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    <AccessTime sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                    Select a Time
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 3 }}>
                    {timeSlots.map(time => (
                      <Grid item key={time}>
                        <Chip
                          label={time}
                          onClick={() => setSelectedTime(time)}
                          color={selectedTime === time ? 'primary' : 'default'}
                          variant={selectedTime === time ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              <Button
                variant="contained" size="large" fullWidth
                disabled={!selectedDate || !selectedTime}
                onClick={() => setActiveStep(1)}
                sx={{ mt: 2, py: 1.5 }}
              >
                Continue
              </Button>
            </Box>
          )}

          {/* Step 1: Confirm */}
          {activeStep === 1 && (
            <Box>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Booking Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Teacher</Typography>
                      <Typography fontWeight={600}>{teacher.displayName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Language</Typography>
                      <Typography fontWeight={600}>{selectedLanguage}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Date</Typography>
                      <Typography fontWeight={600}>
                        {(() => {
                        const [y, mo, d] = selectedDate.split('-').map(Number);
                        return new Date(y, mo - 1, d).toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric' });
                      })()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Time</Typography>
                      <Typography fontWeight={600}>{selectedTime}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography fontWeight={600}>{teacher.lessonDuration || 50} minutes</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Type</Typography>
                      <Chip label={isTrial ? 'Trial' : 'Regular'} size="small" color={isTrial ? 'info' : 'primary'} />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {price === 0 ? 'Free' : `${currSymbol}${price}`}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <TextField
                fullWidth label="Notes for the teacher (optional)"
                multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Tell the teacher what you'd like to focus on..."
                sx={{ mb: 3 }}
              />

              <Alert severity="info" sx={{ mb: 3 }}>
                {price > 0
                  ? 'Payment will be processed after the teacher confirms your booking. A video meeting link will be sent to both of you.'
                  : 'A video meeting link will be sent to both you and the teacher once the booking is confirmed.'}
              </Alert>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={() => setActiveStep(0)} sx={{ flex: 1 }}>
                  Back
                </Button>
                <Button
                  variant="contained" onClick={handleBooking}
                  disabled={booking} sx={{ flex: 2, py: 1.5 }}
                  startIcon={booking ? <CircularProgress size={20} /> : <Payment />}
                >
                  {booking ? 'Processing...' : `Confirm Booking${price > 0 ? ` - ${currSymbol}${price}` : ''}`}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Success */}
          {activeStep === 2 && (
            <Box textAlign="center" py={4}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Booking Confirmed!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your lesson with {teacher.displayName} has been requested.
                You'll receive a video meeting link once the teacher confirms.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => navigate(`/${lang}/student/dashboard`)}>
                  Go to Dashboard
                </Button>
                <Button variant="contained" onClick={() => navigate(`/${lang}/student/search`)}>
                  Browse More Teachers
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default BookLesson;
